import type { ChecklistOverview } from '@acasa/shared'
import { useEffect, useRef } from 'react'
import { CheckList, Popover } from '@/components/ui/popover'
import { useFacetOptions } from './facets'
import {
  ACTIVE_STATUSES,
  ATTRIBUTE_COLUMNS,
  type AttributeKey,
  type Completeness,
  type TableState,
} from './use-table-state'

const COMPLETENESS: { value: Completeness; label: string }[] = [
  { value: 'incomplete', label: 'Unvollständig' },
  { value: 'complete', label: 'Vollständig' },
  { value: 'all', label: 'Alle' },
]

export function Toolbar({
  data,
  state,
  patch,
  resetFilters,
  visibleCount,
  onExport,
}: {
  data: ChecklistOverview
  state: TableState
  patch: (changes: Partial<TableState>) => void
  resetFilters: () => void
  visibleCount: number
  onExport: () => void
}) {
  const searchInput = useRef<HTMLInputElement>(null)

  // "/" to search is the muscle memory for a tool used all day.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const typing = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA'

      if (event.key === '/' && !typing) {
        event.preventDefault()
        searchInput.current?.focus()
      }
      if (event.key === 'Escape' && target === searchInput.current) {
        patch({ search: '' })
        searchInput.current?.blur()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [patch])

  const options = useFacetOptions(data)

  // Scope (what the tool is about) is counted separately from ad-hoc filters,
  // because "Zurücksetzen" clears only the latter.
  const activeFilters =
    state.agent.length +
    state.type.length +
    state.city.length +
    state.missingDocuments.length +
    (state.search ? 1 : 0)

  return (
    <div className="border-line bg-surface z-20 shrink-0 border-b">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
        <div className="relative min-w-[13rem] flex-1">
          <input
            ref={searchInput}
            value={state.search}
            onChange={(event) => patch({ search: event.target.value })}
            placeholder="ImmoNr, Objekt, Ort, Betreuer …"
            className="border-line focus:border-brand bg-canvas placeholder:text-faint w-full border px-2.5 py-1.5 pr-8 text-xs transition-colors outline-none"
          />
          <kbd className="text-faint border-line pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 border px-1 font-mono text-[10px]">
            /
          </kbd>
        </div>

        <div className="border-line flex border">
          {COMPLETENESS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => patch({ completeness: option.value })}
              className={`px-2.5 py-1.5 text-xs transition-colors ${
                state.completeness === option.value
                  ? 'bg-brand text-surface'
                  : 'text-muted hover:text-ink'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <Popover label="Vermarktung" badge={state.marketingType.length}>
          {() => (
            <>
              <p className="text-faint border-line border-b px-3 py-2 text-[11px] leading-snug">
                Die Unterlagen-Checkliste wird nur bei Kaufobjekten gepflegt.
              </p>
              <CheckList
                options={options.marketingType}
                selected={state.marketingType}
                onChange={(marketingType) => patch({ marketingType })}
              />
            </>
          )}
        </Popover>

        <Popover label="Status" badge={state.status.length} width="w-72">
          {() => (
            <>
              <div className="border-line flex items-center justify-between border-b px-3 py-1.5">
                <span className="text-faint text-[10px] tracking-wider uppercase">
                  Vermarktungsphase
                </span>
                <span className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => patch({ status: ACTIVE_STATUSES })}
                    className="text-brand text-[11px] underline underline-offset-2"
                  >
                    nur aktive
                  </button>
                  <button
                    type="button"
                    onClick={() => patch({ status: [] })}
                    className="text-brand text-[11px] underline underline-offset-2"
                  >
                    alle
                  </button>
                </span>
              </div>
              <CheckList
                options={options.status}
                selected={state.status}
                onChange={(status) => patch({ status })}
                emptyLabel="Noch nicht abgeglichen"
              />
            </>
          )}
        </Popover>

        <Popover label="Aktiv" badge={state.activeState.length}>
          {() => (
            <CheckList
              options={options.activeState}
              selected={state.activeState}
              onChange={(activeState) => patch({ activeState })}
            />
          )}
        </Popover>

        {/* everything from here on is an ad-hoc filter that "Zurücksetzen" clears */}
        <span className="bg-line mx-0.5 h-5 w-px" aria-hidden />

        <Popover label="Betreuer" badge={state.agent.length}>
          {() => (
            <CheckList
              options={options.agent}
              selected={state.agent}
              onChange={(agent) => patch({ agent })}
            />
          )}
        </Popover>

        <Popover label="Art" badge={state.type.length}>
          {() => (
            <CheckList
              options={options.type}
              selected={state.type}
              onChange={(type) => patch({ type })}
            />
          )}
        </Popover>

        <Popover label="Ort" badge={state.city.length}>
          {() => (
            <CheckList
              options={options.city}
              selected={state.city}
              onChange={(city) => patch({ city })}
            />
          )}
        </Popover>

        <Popover label="Fehlendes Dokument" badge={state.missingDocuments.length} width="w-80">
          {() => (
            <>
              <p className="text-faint border-line border-b px-3 py-2 text-[11px] leading-snug">
                Zeigt nur Objekte, bei denen diese Unterlagen offen sind.
              </p>
              <CheckList
                options={data.documents.map((document) => ({
                  value: document.key,
                  label: document.label,
                  hint: `${document.openCount}`,
                }))}
                selected={state.missingDocuments}
                onChange={(missingDocuments) => patch({ missingDocuments })}
              />
            </>
          )}
        </Popover>

        {activeFilters > 0 && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-muted hover:text-danger px-1.5 py-1.5 text-xs underline underline-offset-2"
          >
            Zurücksetzen
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-faint tnum font-mono text-[11px]">
            {visibleCount}/{data.estates.length}
          </span>

          <Popover label="Spalten" align="right" width="w-72">
            {() => (
              <>
                <div className="border-line border-b py-1">
                  {ATTRIBUTE_COLUMNS.map((column) => (
                    <label
                      key={column.key}
                      className="hover:bg-canvas flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={state.visibleAttributes.includes(column.key)}
                        onChange={() =>
                          patch({
                            visibleAttributes: state.visibleAttributes.includes(column.key)
                              ? state.visibleAttributes.filter((key) => key !== column.key)
                              : ([...state.visibleAttributes, column.key] as AttributeKey[]),
                          })
                        }
                        className="accent-brand size-3.5"
                      />
                      <span className="text-ink">{column.label}</span>
                    </label>
                  ))}
                </div>

                <label className="hover:bg-canvas flex cursor-pointer items-center gap-2 px-3 py-2 text-xs">
                  <input
                    type="checkbox"
                    checked={state.showMatrix}
                    onChange={() => patch({ showMatrix: !state.showMatrix })}
                    className="accent-brand size-3.5"
                  />
                  <span className="text-ink font-medium">Dokumenten-Matrix</span>
                </label>

                {state.showMatrix && (
                  <>
                    <div className="border-line flex items-center justify-between border-t px-3 py-1.5">
                      <span className="text-faint text-[10px] tracking-wider uppercase">
                        Dokumente
                      </span>
                      <button
                        type="button"
                        onClick={() => patch({ visibleDocuments: [] })}
                        className="text-brand text-[11px] underline underline-offset-2"
                      >
                        alle
                      </button>
                    </div>
                    <CheckList
                      options={data.documents.map((document) => ({
                        value: document.key,
                        label: document.label,
                      }))}
                      selected={
                        state.visibleDocuments.length === 0
                          ? data.documents.map((document) => document.key)
                          : state.visibleDocuments
                      }
                      onChange={(next) =>
                        patch({
                          visibleDocuments: next.length === data.documents.length ? [] : next,
                        })
                      }
                    />
                  </>
                )}
              </>
            )}
          </Popover>

          <button
            type="button"
            onClick={() => patch({ compact: !state.compact })}
            title={state.compact ? 'Zeilenhöhe vergrößern' : 'Kompakte Zeilen'}
            className="border-line bg-surface text-muted hover:text-ink border px-2 py-1.5 text-xs transition-colors"
          >
            {state.compact ? '≡' : '☰'}
          </button>

          <button
            type="button"
            onClick={onExport}
            className="border-line bg-surface text-muted hover:text-ink border px-2.5 py-1.5 text-xs transition-colors"
          >
            CSV
          </button>
        </div>
      </div>
    </div>
  )
}
